import { useState, useRef } from 'react'
import { PlaneLanding, PlaneTakeoff, Building, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Settings, Map, AlertCircle, Clock, LayoutTemplate, Spline, CalendarClock, Columns, List, Fuel, Utensils, Zap, Snowflake, Droplets, ChevronDown as ExpandIcon, ChevronUp as CollapseIcon } from 'lucide-react'
import { format } from 'date-fns'
import {
  needsAttention,
  arrivals,
  departures,
  stats,
} from './data/mockOpsDashboardData'
import type { DashboardItem } from './data/mockOpsDashboardData'
import { cn } from '@/lib/utils'

type LayoutMode = 'hybrid' | 'sidebar'
type ScheduleMode = 'split' | 'timeline' | 'center-timeline'

export function OpsDashboardMock() {
  const [isAttentionOpen, setIsAttentionOpen] = useState(true)
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('hybrid')
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('split')
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const scrollAttention = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
        const scrollAmount = 300
        scrollContainerRef.current.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth'
        })
    }
  }

  return (
    <div className="flex flex-col h-screen bg-slate-50 overflow-hidden font-sans">
      {/* 1. Header Bar */}
      <header className="flex-none bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between z-20 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-slate-500 hover:text-slate-800 cursor-pointer transition-colors">
             <span className="font-semibold text-slate-400">Yesterday</span>
          </div>
          <div className="flex flex-col items-center">
             <h1 className="text-xl font-bold text-slate-900 tracking-tight">Today, {format(new Date(), 'MMM d')}</h1>
          </div>
          <div className="flex items-center gap-2 text-slate-500 hover:text-slate-800 cursor-pointer transition-colors">
             <span className="font-semibold text-slate-400">Tomorrow</span>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
             <button className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-md transition-colors">
                <Map className="w-4 h-4" />
                Ramp View
            </button>
            <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors">
                <Settings className="w-5 h-5" />
            </button>
        </div>
      </header>

      {/* 2. Counters Bar & Controls */}
      <div className="flex-none bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between gap-6 z-10 shadow-sm overflow-x-auto">
        <Counter
          icon={AlertCircle}
          count={stats.needsAttention}
          label="Needs Attn"
          color="text-red-600 bg-red-50 border-red-100"
          onClick={() => {
            setIsAttentionOpen(true)
            scrollToSection('attention-section')
          }}
        />
        <Counter
          icon={PlaneLanding}
          count={stats.arrivals}
          label="Arrivals"
          color="text-blue-600 bg-blue-50 border-blue-100"
          onClick={() => scrollToSection('schedule-section')}
        />
        <Counter
          icon={Building}
          count={stats.onGround}
          label="On Ground"
          color="text-slate-600 bg-slate-50 border-slate-100"
        />
        <Counter
          icon={PlaneTakeoff}
          count={stats.departures}
          label="Departures"
          color="text-emerald-600 bg-emerald-50 border-emerald-100"
           onClick={() => scrollToSection('schedule-section')}
        />
        
        <div className="h-8 w-px bg-slate-200 mx-2" />
        
        {/* View Controls */}
        <div className="flex items-center gap-3 ml-auto">
             <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
                <button 
                    onClick={() => setLayoutMode('hybrid')}
                    className={cn(
                        "p-1.5 rounded-md transition-all flex items-center gap-2 text-xs font-semibold",
                        layoutMode === 'hybrid' ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
                    )}
                    title="Layout: Hybrid Banner"
                >
                    <LayoutTemplate className="w-4 h-4" />
                    <span className="hidden xl:inline">Hybrid</span>
                </button>
                <button 
                    onClick={() => setLayoutMode('sidebar')}
                    className={cn(
                        "p-1.5 rounded-md transition-all flex items-center gap-2 text-xs font-semibold",
                        layoutMode === 'sidebar' ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
                    )}
                    title="Layout: Sidebar"
                >
                    <Columns className="w-4 h-4" />
                    <span className="hidden xl:inline">Sidebar</span>
                </button>
            </div>

             <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
                <button 
                    onClick={() => setScheduleMode('split')}
                    className={cn(
                        "p-1.5 rounded-md transition-all flex items-center gap-2 text-xs font-semibold",
                        scheduleMode === 'split' ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
                    )}
                    title="Schedule: Split Lists"
                >
                    <List className="w-4 h-4" />
                    <span className="hidden xl:inline">Split</span>
                </button>
                 <button 
                    onClick={() => setScheduleMode('center-timeline')}
                    className={cn(
                        "p-1.5 rounded-md transition-all flex items-center gap-2 text-xs font-semibold",
                        scheduleMode === 'center-timeline' ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
                    )}
                    title="Schedule: Center Timeline (Tree)"
                >
                    <Spline className="w-4 h-4" />
                    <span className="hidden xl:inline">Tree</span>
                </button>
                <button 
                    onClick={() => setScheduleMode('timeline')}
                    className={cn(
                        "p-1.5 rounded-md transition-all flex items-center gap-2 text-xs font-semibold",
                        scheduleMode === 'timeline' ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
                    )}
                    title="Schedule: Linear Timeline"
                >
                    <CalendarClock className="w-4 h-4" />
                    <span className="hidden xl:inline">Timeline</span>
                </button>
            </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={cn(
          "flex-1 overflow-hidden flex",
          layoutMode === 'sidebar' ? "flex-row" : "flex-col"
      )}>
        
        {/* Needs Attention Section */}
         <div 
            id="attention-section" 
            className={cn(
                "flex-none transition-all duration-300 ease-in-out z-10",
                layoutMode === 'hybrid' ? "w-full px-6 pt-6" : "w-[380px] h-full border-r border-slate-200 bg-white overflow-y-auto"
            )}
        >
            <div className={cn(
                "bg-white transition-all duration-300 overflow-hidden relative group",
                layoutMode === 'hybrid' 
                    ? cn(
                        "rounded-xl shadow-sm border border-slate-200", 
                        isAttentionOpen ? "ring-2 ring-red-100" : "hover:shadow-md"
                      )
                    : "h-full" // Sidebar style
            )}>
                <div 
                    className={cn(
                        "px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100",
                        layoutMode === 'sidebar' && "sticky top-0 bg-white z-10 border-b shadow-sm"
                    )}
                    onClick={() => setIsAttentionOpen(!isAttentionOpen)}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                             <AlertCircle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900">Needs Attention</h2>
                            {!isAttentionOpen && layoutMode === 'hybrid' && (
                                <p className="text-sm text-slate-500">{needsAttention.length} items requiring action</p>
                            )}
                        </div>
                         {(isAttentionOpen || layoutMode === 'sidebar') && (
                            <span className="ml-2 px-2.5 py-0.5 pt-px bg-red-100 text-red-700 text-xs font-bold rounded-full">
                                {needsAttention.length}
                            </span>
                        )}
                    </div>
                     {layoutMode === 'hybrid' && (
                        isAttentionOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />
                     )}
                </div>

                {(isAttentionOpen || layoutMode === 'sidebar') && (
                    <div className="relative">
                        {/* Scroll Buttons for Hybrid Mode */}
                        {layoutMode === 'hybrid' && (
                            <>
                                <button 
                                    onClick={() => scrollAttention('left')}
                                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 shadow-md border border-slate-200 rounded-full text-slate-600 hover:text-slate-900 hover:scale-110 transition-all z-20 opacity-0 group-hover:opacity-100"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button 
                                    onClick={() => scrollAttention('right')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 shadow-md border border-slate-200 rounded-full text-slate-600 hover:text-slate-900 hover:scale-110 transition-all z-20 opacity-0 group-hover:opacity-100"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </>
                        )}
                        
                        <div 
                            ref={scrollContainerRef}
                            className={cn(
                                "p-4",
                                layoutMode === 'hybrid' 
                                    ? "flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-6" 
                                    : "space-y-3 bg-red-50/10"
                            )}
                        >
                            {needsAttention.map((item) => (
                                <div key={item.id} className={cn(layoutMode === 'hybrid' ? "snap-start min-w-[350px] max-w-[400px]" : "")}>
                                    <AttentionCard item={item} compact={layoutMode === 'sidebar'} />
                                </div>
                            ))}
                        </div>
                         {layoutMode === 'sidebar' && (
                             <div className="py-2 flex justify-center">
                                <div className="text-xs text-slate-400 uppercase tracking-wide font-medium">All items shown</div>
                             </div>
                         )}
                    </div>
                )}
            </div>
        </div>

        {/* Schedule Area */}
        <main className={cn(
            "flex-1 overflow-y-auto p-6 scroll-smooth",
            layoutMode === 'sidebar' ? "bg-slate-50" : ""
        )}>
            <div id="schedule-section" className={cn(
                "pb-20",
                scheduleMode === 'center-timeline' ? "max-w-6xl mx-auto" : "",
                scheduleMode === 'timeline' ? "max-w-4xl mx-auto" : "",
                scheduleMode === 'split' ? "grid grid-cols-1 md:grid-cols-2 gap-6" : ""
            )}>
                
                {scheduleMode === 'split' && (
                    <>
                        {/* Arrivals Column */}
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-full self-start">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-t-xl sticky top-0 z-10">
                                <div className="flex items-center gap-2">
                                     <div className="p-1.5 bg-blue-100 rounded-md">
                                        <PlaneLanding className="w-4 h-4 text-blue-600" />
                                     </div>
                                    <h2 className="text-lg font-bold text-slate-800">Arrivals</h2>
                                    <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded-full">{arrivals.length}</span>
                                </div>
                            </div>
                            <div className="divide-y divide-slate-100">
                                {arrivals.map((item) => (
                                    <ScheduleRow key={item.id} item={item} />
                                ))}
                            </div>
                        </div>

                        {/* Departures Column */}
                         <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-full self-start">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-t-xl sticky top-0 z-10">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 bg-emerald-100 rounded-md">
                                        <PlaneTakeoff className="w-4 h-4 text-emerald-600" />
                                    </div>
                                    <h2 className="text-lg font-bold text-slate-800">Departures</h2>
                                     <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded-full">{departures.length}</span>
                                </div>
                            </div>
                            <div className="divide-y divide-slate-100">
                                {departures.map((item) => (
                                    <ScheduleRow key={item.id} item={item} />
                                ))}
                            </div>
                        </div>
                    </>
                )}

                {scheduleMode === 'timeline' && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-full">
                         <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-t-xl sticky top-0 z-10">
                                <div className="flex items-center gap-2">
                                     <div className="p-1.5 bg-slate-100 rounded-md">
                                        <CalendarClock className="w-4 h-4 text-slate-600" />
                                     </div>
                                    <h2 className="text-lg font-bold text-slate-800">Chronological Timeline</h2>
                                </div>
                        </div>
                        <div className="divide-y divide-slate-100">
                            {[...arrivals, ...departures]
                                .sort((a, b) => a.time.localeCompare(b.time))
                                .map(item => (
                                    <ScheduleRow key={item.id} item={item} isTimeline />
                                ))
                            }
                        </div>
                    </div>
                )}

                {scheduleMode === 'center-timeline' && (
                    <div className="relative">
                         {/* Center Line */}
                         <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-200 -translate-x-1/2" />
                         
                         <div className="space-y-8 py-8">
                            {[...arrivals, ...departures]
                                .sort((a, b) => a.time.localeCompare(b.time))
                                .map((item, index) => (
                                    <CenterTimelineRow key={item.id} item={item} index={index} />
                                ))
                            }
                         </div>
                    </div>
                )}
            </div>
      </main>
    </div>

       {/* Floating Action Bar */}
       <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-md border border-slate-200 shadow-lg px-6 py-3 rounded-full flex items-center gap-4 z-50">
            <button className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">+ Walk-In</button>
            <div className="w-px h-4 bg-slate-300"></div>
            <button className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">+ Check In</button>
             <div className="w-px h-4 bg-slate-300"></div>
            <button className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">+ Check Out</button>
       </div>

    </div>
  )
}

// Subcomponents

function Counter({
  icon: Icon,
  count,
  label,
  color,
  onClick,
}: {
  icon: any
  count: number
  label: string
  color: string
  onClick?: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-4 px-5 py-3 rounded-xl border transition-all duration-200 group flex-1 min-w-[160px]",
        color,
        "hover:shadow-md hover:brightness-95 hover:translate-y-[-1px]"
      )}
    >
      <div className="p-3 bg-white/60 rounded-lg group-hover:bg-white/80 transition-colors">
        <Icon className="w-6 h-6 opacity-90" />
      </div>
      <div className="text-left">
        <div className="text-2xl font-bold leading-none tracking-tight">{count}</div>
        <div className="text-xs font-semibold uppercase opacity-70 mt-1 tracking-wide">{label}</div>
      </div>
    </button>
  )
}

function AttentionCard({ item }: { item: DashboardItem, compact?: boolean }) {
  const isCritical = item.status === 'critical'
  
  return (
    <div className={cn(
        "flex flex-col gap-3 p-4 rounded-lg border-l-4 shadow-sm bg-white transition-all hover:shadow-md h-full",
        isCritical ? "border-red-500" : "border-amber-500"
    )}>
      {/* Header Line */}
      <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
             <div className={cn("p-1.5 rounded-full shrink-0", isCritical ? "bg-red-50" : "bg-amber-50")}>
                {isCritical ? <AlertCircle className="w-4 h-4 text-red-600" /> : <Clock className="w-4 h-4 text-amber-600" />}
             </div>
             <div>
                <div className="font-bold text-lg text-slate-900 leading-tight">{item.tail}</div>
                <div className="text-xs text-slate-500 font-medium">{item.type} · {item.location}</div>
             </div>
          </div>
          <div className="text-xs font-bold text-slate-400 bg-slate-50 px-2 py-1 rounded">
            {item.time}
          </div>
      </div>

      {/* Warnings List */}
      <div className="space-y-2 flex-1">
         {item.warnings?.map(w => (
             <div key={w.id} className="flex items-center justify-between gap-3 bg-slate-50 p-2 rounded border border-slate-100">
                <div className="flex items-center gap-2 min-w-0">
                    <div className={cn(
                        "w-1.5 h-1.5 rounded-full shrink-0",
                        w.level === 'critical' ? "bg-red-500" : "bg-amber-500"
                    )} />
                    <span className={cn(
                        "text-sm font-medium truncate",
                        w.level === 'critical' ? "text-red-900" : "text-slate-700"
                    )}>{w.message}</span>
                </div>
                {w.action && (
                    <button className={cn(
                        "text-xs font-bold px-2 py-1 rounded transition-colors whitespace-nowrap",
                        w.level === 'critical' 
                            ? "bg-red-100 text-red-700 hover:bg-red-200" 
                            : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                    )}>
                        {w.action}
                    </button>
                )}
             </div>
         ))}
      </div>
    </div>
  )
}

function ScheduleRow({ item, isTimeline }: { item: DashboardItem, isTimeline?: boolean }) {
    const isArrival = item.isArrival
    const [isExpanded, setIsExpanded] = useState(false)
    const hasServices = item.services && item.services.length > 0

    return (
        <div className="group flex flex-col p-4 hover:bg-slate-50 transition-colors cursor-default gap-4 border-l-4 border-transparent hover:border-slate-300">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0">
                <div className="flex items-start sm:items-center gap-4">
                     <div className={cn(
                         "flex flex-col items-center justify-center w-14 h-14 rounded-lg border transition-colors shrink-0",
                         isTimeline 
                            ? (isArrival ? "bg-blue-50 border-blue-100" : "bg-emerald-50 border-emerald-100")
                            : "bg-slate-100 border-slate-200"
                     )}>
                        <span className="text-lg font-bold text-slate-900">{item.time}</span>
                        {isTimeline && (
                            <div className="mt-0.5">
                                {isArrival 
                                    ? <PlaneLanding className="w-3 h-3 text-blue-500" />
                                    : <PlaneTakeoff className="w-3 h-3 text-emerald-500" />
                                }
                            </div>
                        )}
                     </div>
                     
                     <div className="min-w-0">
                        <div className="flex items-baseline gap-2">
                            <span className="font-bold text-lg text-slate-900">{item.tail}</span>
                            <span className="text-sm font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded text-xs shrink-0">{item.type}</span>
                        </div>
                        
                        <div className="flex items-center gap-1.5 text-sm text-slate-500 font-medium mt-0.5 flex-wrap">
                            {isArrival ? (
                                 <>
                                    <span className="text-slate-400">from</span>
                                    <span className="text-slate-700 font-semibold">{item.origin}</span>
                                    {item.location && (
                                        <>
                                            <span className="text-slate-300 mx-1">•</span>
                                            <span className="text-slate-600">to {item.location}</span>
                                        </>
                                    )}
                                 </>
                            ) : (
                                <>
                                    <span className="text-slate-400">to</span>
                                    <span className="text-slate-700 font-semibold">{item.destination}</span>
                                    {item.description && (
                                        <>
                                            <span className="text-slate-300 mx-1">•</span>
                                            <span className="text-slate-600">{item.description}</span>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                     </div>
                </div>

                <div className="flex items-center gap-3 pl-18 sm:pl-0 self-end sm:self-auto">
                     {hasServices && (
                        <button 
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors"
                        >
                            {isExpanded ? <CollapseIcon className="w-4 h-4" /> : <ExpandIcon className="w-4 h-4" />}
                        </button>
                     )}
                    <button className={cn(
                        "px-3 py-1.5 text-sm font-medium rounded-md transition-colors border shadow-sm whitespace-nowrap",
                        isArrival 
                            ? "bg-white border-blue-200 text-blue-700 hover:bg-blue-50" 
                            : "bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    )}>
                        {item.actionLabel}
                    </button>
                </div>
            </div>

            {/* Expandable Service Timeline */}
            {hasServices && (isExpanded || true) && ( 
                <div className="mt-2 pl-[72px] animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="relative pt-4 pb-2">
                        {/* Timeline Line */}
                        <div className="absolute left-0 right-0 top-[27px] h-0.5 bg-slate-100" />
                        
                        <div className="flex gap-8 relative z-10 overflow-x-auto pb-2 scrollbar-hide">
                            {item.services?.map((service, _idx) => (
                                <div key={service.id} className="flex flex-col items-center gap-2 min-w-[80px]">
                                    <div className={cn(
                                        "w-2.5 h-2.5 rounded-full ring-4 ring-white",
                                        getStatusColor(service.status)
                                    )} />
                                    <div className="flex flex-col items-center text-center">
                                        <div className="p-1.5 bg-slate-50 border border-slate-100 rounded-md mb-1">
                                            {getServiceIcon(service.name)}
                                        </div>
                                        <div className="text-xs font-bold text-slate-700">{service.name}</div>
                                        <div className="text-[10px] text-slate-400 font-medium whitespace-nowrap">{service.time || 'Pending'}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

function CenterTimelineRow({ item }: { item: DashboardItem, index: number }) {
    const isLeft = item.isArrival // Arrivals on left
    
    return (
        <div className="relative flex items-center justify-center w-full">
            {/* Center Time Node */}
            <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center z-10 bg-slate-50 py-2">
                 <div className="text-xs font-bold text-slate-400 mb-1">{item.time}</div>
                 <div className={cn(
                     "w-3 h-3 rounded-full border-2",
                     isLeft ? "bg-blue-500 border-blue-100" : "bg-emerald-500 border-emerald-100"
                 )} />
            </div>

            {/* Connector Line */}
            <div className={cn(
                "absolute top-1/2 h-px bg-slate-300 w-1/2 z-0",
                isLeft ? "right-1/2 pr-12 origin-right" : "left-1/2 pl-12 origin-left"
            )} />

            <div className={cn(
                "w-[45%] flex flex-col gap-2",
                isLeft ? "mr-auto items-end text-right pr-12" : "ml-auto items-start text-left pl-12"
            )}>
                <div className={cn(
                    "bg-white p-4 rounded-xl shadow-sm border transition-all hover:shadow-md w-full max-w-md",
                    isLeft ? "border-l-4 border-l-blue-500" : "border-r-4 border-r-emerald-500"
                )}>
                    <div className="flex justify-between items-start mb-2">
                        <span className="font-bold text-lg">{item.tail}</span>
                        <div className={cn(
                            "px-2 py-0.5 rounded text-xs font-bold uppercase",
                            isLeft ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"
                        )}>
                            {isLeft ? "Arrival" : "Departure"}
                        </div>
                    </div>
                    <div className="text-sm text-slate-600 mb-3">
                         {isLeft ? `From ${item.origin}` : `To ${item.destination}`} · {item.type}
                    </div>
                    
                    {/* Compact Services */}
                    {item.services && item.services.length > 0 && (
                        <div className={cn("flex gap-2", isLeft ? "justify-end" : "justify-start")}>
                            {item.services.map(s => (
                                <div key={s.id} className="p-1 rounded bg-slate-50 border border-slate-100" title={`${s.name} - ${s.status}`}>
                                    {getServiceIcon(s.name)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

function getServiceIcon(name: string) {
    switch (name.toLowerCase()) {
        case 'fuel': return <Fuel className="w-3 h-3 text-amber-600" />
        case 'lav': return <Droplets className="w-3 h-3 text-blue-400" />
        case 'catering': return <Utensils className="w-3 h-3 text-orange-400" />
        case 'gpu': return <Zap className="w-3 h-3 text-yellow-500" />
        case 'ice': return <Snowflake className="w-3 h-3 text-sky-400" />
        default: return <div className="w-3 h-3 rounded-full bg-slate-200" />
    }
}

function getStatusColor(status: string) {
    switch (status) {
        case 'completed': return "bg-green-500"
        case 'in-progress': return "bg-blue-500"
        case 'requested': return "bg-amber-400 active:ring-amber-200"
        default: return "bg-slate-300"
    }
}
